
import { useState, useEffect, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { BOOKING_STATUS, COMMISSION_RATE, formatCurrencyByCountry, formatDate } from '../lib/constants'
import { calculateRefund } from '../lib/cancellation'
import VerifyEmailPrompt from '../components/VerifyEmailPrompt'
import Header from '../components/Header'
import ReviewModal from './Reviews'
import ListingPlaceholder from '../components/ListingPlaceholder'

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51TKvQ2GoEyYHYqGSZvHLSvnXeGcerN9SEFv4r6djEtRgxMnuJRTVV2lnMlNFHMdZnl9eH3yJYPXLIJI6hGADy3200dzGvlNmR')

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

const DECLINE_REASONS = [
'Sorry, this item is already out on hire for those dates.',
'Sorry, this item is currently unavailable.',
'Sorry, I am unable to hire this item at this time.',
'Sorry, the hire period requested does not suit my availability.',
'Sorry, I have decided not to hire this item out at this time.',
]

const PROBLEM_REASONS = [
'Item would not start or power on',
'Item broke down during use',
'Item was not as described in the listing',
'Item was damaged or unsafe to use',
'Item was not available at agreed time',
'Other issue',
]

export default function MyBookings() {
const { user, profile } = useAuth()
const navigate = useNavigate()
const location = useLocation()
const [bookings, setBookings] = useState([])
const [offers, setOffers] = useState([])
const [loading, setLoading] = useState(true)
const [tab, setTab] = useState(() => {
const params = new URLSearchParams(location.search)
const tabParam = params.get('tab')
if (tabParam === 'owner' || tabParam === 'hirer') return tabParam
return 'owner'
})
const [declineModal, setDeclineModal] = useState(null)
const [reviewModal, setReviewModal] = useState(null)
const [disputeModal, setDisputeModal] = useState(null)
const [cancelModal, setCancelModal] = useState(null)
const [cancelReason, setCancelReason] = useState('')
const [cancelling, setCancelling] = useState(false)
const [selectedReason, setSelectedReason] = useState(DECLINE_REASONS[0])
const [selectedProblem, setSelectedProblem] = useState(PROBLEM_REASONS[0])
const [customReason, setCustomReason] = useState('')
const [customProblem, setCustomProblem] = useState('')
const [processing, setProcessing] = useState(false)
const [stripeBlockModal, setStripeBlockModal] = useState(null)
const [verifyBlockModal, setVerifyBlockModal] = useState(null)
const [reviewedBookings, setReviewedBookings] = useState([])
const [disputedBookings, setDisputedBookings] = useState([])
const [payModal, setPayModal] = useState(null)
const [payProcessing, setPayProcessing] = useState(false)
const [payError, setPayError] = useState('')
const [showReferralModal, setShowReferralModal] = useState(false)
const [referralCopied, setReferralCopied] = useState(false)
const referralCheckedRef = useRef(false)

useEffect(() => {
if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
fetchBookings()
fetchReviewedBookings()
fetchOffers()
}, [tab])

useEffect(() => {
if (!user || !profile) return
if (referralCheckedRef.current) return
if (profile.referral_prompt_seen_at) return
referralCheckedRef.current = true

const checkCompletedOwnerBooking = async () => {
const { count } = await supabase
.from('bookings')
.select('id', { count: 'exact', head: true })
.eq('owner_id', user.id)
.eq('status', 'completed')
if (!count) return

setShowReferralModal(true)
supabase
.from('profiles')
.update({ referral_prompt_seen_at: new Date().toISOString() })
.eq('id', user.id)
.then(() => {})
}
checkCompletedOwnerBooking()
}, [user, profile])

const referralLink = profile?.referral_code
? `https://hireitnow.au/auth?mode=signup&ref=${profile.referral_code}`
: null
const copyReferralLink = () => {
if (!referralLink) return
navigator.clipboard.writeText(referralLink)
setReferralCopied(true)
setTimeout(() => setReferralCopied(false), 2000)
}

const fetchBookings = async () => {
setLoading(true)
const col = tab === 'hirer' ? 'hirer_id' : 'owner_id'
const { data } = await supabase
.from('bookings')
.select('*, listing_id, listings(title, postcode, images, country, category_id)')
.eq(col, user.id)
.order('created_at', { ascending: false })
const rows = data || []
const ids = [...new Set(rows.flatMap(b => [b.hirer_id, b.owner_id]).filter(Boolean))]
let nameMap = {}
if (ids.length) {
const { data: profs } = await supabase.from('public_profiles').select('id, full_name').in('id', ids)
;(profs || []).forEach(p => { nameMap[p.id] = p })
}
const withNames = rows.map(b => ({ ...b, hirer: nameMap[b.hirer_id] || null, owner: nameMap[b.owner_id] || null }))
setBookings(withNames)
setDisputedBookings(withNames.filter(b => b.dispute_reason).map(b => b.id) || [])
setLoading(false)
}

const fetchOffers = async () => {
const col = tab === 'hirer' ? 'hirer_id' : 'owner_id'
const { data } = await supabase
.from('offers')
.select('*, listings(title, images, price_per_hour, category_id)')
.eq(col, user.id)
.eq('status', 'pending')
.order('created_at', { ascending: false })
const rows = data || []
const ids = [...new Set(rows.flatMap(o => [o.hirer_id, o.owner_id]).filter(Boolean))]
let nameMap = {}
if (ids.length) {
const { data: profs } = await supabase.from('public_profiles').select('id, full_name').in('id', ids)
;(profs || []).forEach(p => { nameMap[p.id] = p })
}
const withNames = rows.map(o => ({ ...o, hirer: nameMap[o.hirer_id] || null, owner: nameMap[o.owner_id] || null }))
setOffers(withNames)
}

const fetchReviewedBookings = async () => {
const { data } = await supabase
.from('reviews')
.select('booking_id')
.eq('reviewer_id', user.id)
setReviewedBookings(data?.map(r => r.booking_id) || [])
}

// Owners must connect Stripe before accepting — otherwise the hirer is
// confirmed but can't pay. Checks the database rather than the cached
// profile, so an owner who has just finished Stripe isn't wrongly blocked.
const ownerStripeReady = async () => {
const { data } = await supabase.from('profiles').select('stripe_charges_enabled').eq('id', user.id).single()
return !!data?.stripe_charges_enabled
}

// Owners must also have confirmed their email before accepting (enforced in
// the database too — bookings_require_verified_owner). Fresh read, so
// someone who has just clicked the link isn't blocked by a stale profile.
const ownerEmailVerified = async () => {
const { data } = await supabase.from('profiles').select('email_verified').eq('id', user.id).single()
return !!data?.email_verified
}

const handleAccept = async (booking) => {
setProcessing(true)
if (!(await ownerEmailVerified())) {
setVerifyBlockModal({ hirerName: booking.hirer?.full_name })
setProcessing(false)
return
}
if (!(await ownerStripeReady())) {
setStripeBlockModal({ hirerName: booking.hirer?.full_name })
setProcessing(false)
return
}
// Only confirm if it's still pending. The backend expires unanswered
// requests (72h / start date passed) — if this page was opened before
// that, the booking is no longer pending and nothing should happen.
const { data: accepted, error: acceptErr } = await supabase
.from('bookings')
.update({ status: 'confirmed' })
.eq('id', booking.id)
.eq('status', 'pending')
.select('id')
if (acceptErr?.hint === 'email_not_verified' || /confirm your email/i.test(acceptErr?.message || '')) {
setVerifyBlockModal({ hirerName: booking.hirer?.full_name })
setProcessing(false)
return
}
if (acceptErr || !accepted?.length) {
alert(acceptErr
? 'Could not accept the booking. Please try again.'
: 'This request is no longer available to accept — it has expired or been cancelled.')
setProcessing(false)
fetchBookings()
return
}
await supabase.from('messages').insert({
booking_id: booking.id,
sender_id: user.id,
body: `✅ Great news! Your booking for ${booking.listings?.title} has been confirmed. Please arrange your own insurance before collection. Looking forward to helping you!`,
read: false,
})

// Email notification to hirer
try {
const { data: hirerEmail } = await supabase.rpc('get_user_email', { user_id: booking.hirer_id })
if (hirerEmail) {
const { data: { session } } = await supabase.auth.getSession()
await fetch(`${BACKEND_URL}/confirm-booking`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
hirerEmail,
hirerName: booking.hirer?.full_name,
itemTitle: booking.listings?.title,
ownerName: booking.owner?.full_name || profile?.full_name,
startDate: booking.start_date,
hours: booking.hours,
total: booking.total_amount?.toFixed(2),
})
})
}
} catch (e) {}

setProcessing(false)
fetchBookings()
}

const handleDecline = async () => {
if (!declineModal) return
setProcessing(true)
const reason = customReason.trim() || selectedReason
await supabase.from('bookings').update({ status: 'cancelled' }).eq('id', declineModal.id)
await supabase.from('messages').insert({
booking_id: declineModal.id,
sender_id: user.id,
body: `❌ ${reason}`,
read: false,
})
setDeclineModal(null)
setCustomReason('')
setSelectedReason(DECLINE_REASONS[0])
setProcessing(false)
fetchBookings()
}

const handleCancelBooking = async () => {
if (!cancelModal) return
setCancelling(true)
try {
const isHirerCancelling = cancelModal.hirer_id === user.id
const { refundAmount, feeAmount, policy } = calculateRefund(cancelModal, isHirerCancelling)
const cancelledByLabel = isHirerCancelling ? 'hirer' : 'owner'

// Handle Stripe payment based on cancellation policy
if (cancelModal.stripe_payment_intent_id) {
try {
const { data: { session } } = await supabase.auth.getSession()
if (policy === 'unpaid' || policy === 'owner-cancel' || policy === '48+hr') {
const res = await fetch(`${BACKEND_URL}/stripe/cancel-payment`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({ paymentIntentId: cancelModal.stripe_payment_intent_id })
})
const data = await res.json().catch(() => ({}))
if (res.ok) {
// success — proceed to mark booking cancelled
} else if (data?.code === 'payment_intent_unexpected_state' && data?.paymentIntentStatus === 'canceled') {
// PI was already cancelled (webhook or prior action beat us to it) — safe to proceed
} else if (data?.code === 'payment_intent_unexpected_state') {
// PI in a non-cancellable terminal state (e.g. succeeded/captured)
setCancelling(false)
alert("This booking can't be cancelled — the payment has already been processed. Please contact support at hello@hireitnow.au.")
return
} else {
setCancelling(false)
alert('Could not cancel the booking. Please try again or contact support.')
return
}
} else if (policy === '24-48hr' || policy === 'under-24hr') {
const res = await fetch(`${BACKEND_URL}/stripe/partial-capture`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
paymentIntentId: cancelModal.stripe_payment_intent_id,
amountToCaptureAUD: feeAmount,
})
})
const data = await res.json().catch(() => ({}))
if (res.ok) {
// success — proceed to mark booking cancelled
} else if (data?.code === 'payment_intent_unexpected_state' && data?.paymentIntentStatus === 'canceled') {
// PI was already cancelled — no fee captured, but safe to proceed
} else if (data?.code === 'payment_intent_unexpected_state') {
setCancelling(false)
alert("This booking can't be cancelled — the payment has already been processed. Please contact support at hello@hireitnow.au.")
return
} else {
setCancelling(false)
alert('Could not process the cancellation fee. Please try again or contact support.')
return
}
}
} catch (e) {
console.error('Stripe action error:', e)
}
}

await supabase.from('bookings').update({
status: 'cancelled',
cancelled_at: new Date().toISOString(),
cancelled_by: cancelledByLabel,
cancellation_reason: cancelReason.trim() || null,
refund_owed: refundAmount > 0 ? refundAmount : null,
}).eq('id', cancelModal.id)

let msgBody = ''
if (policy === 'unpaid') {
msgBody = `❌ Booking cancelled by ${cancelledByLabel}.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === 'owner-cancel') {
msgBody = `❌ Booking cancelled by owner. Full refund of $${refundAmount.toFixed(2)} will be processed.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === '48+hr') {
msgBody = `❌ Booking cancelled by hirer (48+ hours before start). Full refund of $${refundAmount.toFixed(2)} released.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === '24-48hr') {
msgBody = `❌ Booking cancelled by hirer (24-48 hours before start). 50% refund of $${refundAmount.toFixed(2)}. $${feeAmount.toFixed(2)} cancellation fee retained.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === 'under-24hr') {
msgBody = `❌ Booking cancelled by hirer (under 24 hours before start). No refund — full cancellation fee of $${feeAmount.toFixed(2)} retained.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
}

await supabase.from('messages').insert({
booking_id: cancelModal.id,
sender_id: user.id,
body: msgBody,
read: false,
})

// Email notification to other party
try {
const otherPersonId = isHirerCancelling ? cancelModal.owner_id : cancelModal.hirer_id
const { data: otherEmail } = await supabase.rpc('get_user_email', { user_id: otherPersonId })
const otherPerson = isHirerCancelling ? cancelModal.owner : cancelModal.hirer
if (otherEmail) {
const { data: { session } } = await supabase.auth.getSession()
await fetch(`${BACKEND_URL}/notify-message`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
recipientEmail: otherEmail,
recipientName: otherPerson?.full_name,
senderName: profile?.full_name || 'A HireIt user',
itemTitle: cancelModal.listings?.title || 'your booking',
messagePreview: `Booking cancelled by ${cancelledByLabel}`,
bookingId: cancelModal.id,
})
})
}
} catch (e) {}

setCancelModal(null)
setCancelReason('')
fetchBookings()
} catch (e) {
console.error('Cancel error:', e)
alert('Failed to cancel booking. Please try again.')
}
setCancelling(false)
}

// Mirrors handleOwnerReturnAgree(true) in Messages.js — keep capture math in sync.
const handleComplete = async (booking) => {
setProcessing(true)
try {
// Refuse to complete an unpaid booking
if (!booking.stripe_payment_intent_id) {
alert('Cannot mark complete: this booking has not been paid.')
return
}

// Idempotency: if already captured (e.g. via the chat-side owner-agree flow), just sync status and open review modal.
// .eq('status', 'confirmed') guards against a concurrent-session race where the booking has since been cancelled-with-fee.
if (booking.payment_captured_at) {
await supabase.from('bookings').update({ status: 'completed' }).eq('id', booking.id).eq('status', 'confirmed')
fetchBookings()
setReviewModal(booking)
return
}

// Capture the hire portion (deposit is auto-released by Stripe on partial capture)
const hireAmount = (booking.total_amount || 0) - (booking.deposit_amount || 0)
const { data: { session } } = await supabase.auth.getSession()
const captureRes = await fetch(`${BACKEND_URL}/stripe/partial-capture`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
paymentIntentId: booking.stripe_payment_intent_id,
amountToCaptureAUD: hireAmount,
})
})
const captureData = await captureRes.json().catch(() => ({}))
if (captureRes.ok) {
// success — proceed to status update
} else if (captureData?.code === 'payment_intent_unexpected_state' && captureData?.paymentIntentStatus === 'succeeded') {
// Race / retry: another path already captured this PI. Safe to mark complete.
console.log('PI already captured, proceeding to status update')
} else {
console.error('Capture failed:', captureData)
alert('Could not release funds: ' + (captureData?.error || 'unknown error'))
return
}

// Status update — only runs after a confirmed-captured payment.
// .eq('status', 'confirmed') prevents racing a concurrent cancellation that set status='cancelled' between our last fetch and now.
await supabase.from('bookings').update({
status: 'completed',
payment_captured_at: new Date().toISOString(),
}).eq('id', booking.id).eq('status', 'confirmed')

// Hirer-facing chat message (sender = owner, matching handleOwnerReturnAgree's pattern)
const depositAmount = booking.deposit_amount || 0
const depositSentence = depositAmount > 0
? `The $${depositAmount.toFixed(2)} hold on your card has been released, and payment for the hire has been sent to the owner.`
: 'Payment for the hire has been sent to the owner.'
const msgBody = `✅ Booking complete! ${depositSentence} Thanks for using HireIt — please leave a review when you have a moment.`
await supabase.from('messages').insert({
booking_id: booking.id,
sender_id: user.id,
body: msgBody,
read: false,
})

fetchBookings()
setReviewModal(booking)
} catch (e) {
console.error('Complete error:', e)
alert('Could not complete the booking. Please try again or contact support.')
} finally {
setProcessing(false)
}
}

const handleDispute = async () => {
if (!disputeModal) return
setProcessing(true)
const reason = customProblem.trim() || selectedProblem
await supabase.from('bookings').update({
dispute_reason: reason,
disputed_at: new Date().toISOString(),
}).eq('id', disputeModal.id)
await supabase.from('messages').insert({
booking_id: disputeModal.id,
sender_id: user.id,
body: `⚠️ Problem reported: ${reason}. A refund request has been submitted.`,
read: false,
})
try {
const { data: adminEmail } = await supabase.rpc('get_user_email', { user_id: user.id })
const { data: ownerEmail } = await supabase.rpc('get_user_email', { user_id: disputeModal.owner_id })
const { data: { session } } = await supabase.auth.getSession()
await fetch(`${BACKEND_URL}/notify-dispute`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
hirerEmail: adminEmail,
hirerName: disputeModal.hirer?.full_name || 'Hirer',
ownerEmail,
itemTitle: disputeModal.listings?.title,
reason,
bookingId: disputeModal.id,
total: disputeModal.total_amount,
})
})
} catch (e) {
console.log('Dispute email failed silently:', e)
}
setDisputeModal(null)
setCustomProblem('')
setSelectedProblem(PROBLEM_REASONS[0])
setProcessing(false)
fetchBookings()
alert('Your problem report has been submitted. Our team will review it within 24 hours.')
}

const handleAcceptOffer = async (offer) => {
setProcessing(true)
if (!(await ownerEmailVerified())) {
setVerifyBlockModal({ hirerName: offer.hirer?.full_name })
setProcessing(false)
return
}
if (!(await ownerStripeReady())) {
setStripeBlockModal({ hirerName: offer.hirer?.full_name })
setProcessing(false)
return
}
if ((offer.hours || 0) > 120) {
alert('This offer is for more than 5 days. Bookings are limited to 5 days at the moment. Please ask the hirer to resubmit for 5 days or less.')
setProcessing(false)
return
}
const totalHours = offer.hours
const offeredPricePerHour = offer.offered_price
const subtotal = offeredPricePerHour * totalHours
const platformFee = subtotal * COMMISSION_RATE
const ownerPayout = subtotal - platformFee
const total = subtotal + (offer.listings?.deposit || 0)

const { data: bookingData } = await supabase.from('bookings').insert({
listing_id: offer.listing_id,
hirer_id: offer.hirer_id,
owner_id: offer.owner_id,
start_date: offer.start_date,
hours: totalHours,
total_amount: total,
platform_fee: platformFee,
owner_payout: ownerPayout,
deposit_amount: offer.listings?.deposit || 0,
status: 'confirmed',
notes: offer.notes,
}).select().single()

await supabase.from('offers').update({ status: 'accepted' }).eq('id', offer.id)

if (bookingData) {
await supabase.from('messages').insert({
booking_id: bookingData.id,
sender_id: user.id,
body: `✅ Great news! Your offer of $${offeredPricePerHour}/hr for ${offer.listings?.title} has been accepted! Total: $${total.toFixed(2)}. Please arrange insurance before collection.`,
read: false,
})
}

setProcessing(false)
fetchOffers()
fetchBookings()
alert('Offer accepted! A confirmed booking has been created.')
}

const handleDeclineOffer = async (offer) => {
setProcessing(true)
await supabase.from('offers').update({ status: 'declined' }).eq('id', offer.id)
setProcessing(false)
fetchOffers()
alert('Offer declined.')
}

const handleReviewSubmit = () => {
setReviewModal(null)
fetchReviewedBookings()
fetchBookings()
}

const reasonLabel = (selected) => ({
display: 'flex',
gap: 10,
cursor: 'pointer',
fontSize: 14,
padding: '8px 12px',
borderRadius: 8,
border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`,
background: selected ? 'var(--primary-light)' : 'white',
transition: 'all 0.15s',
})

const titleLinkStyle = {
fontWeight: 600,
marginBottom: 4,
cursor: 'pointer',
color: 'var(--primary)',
textDecoration: 'none',
}

const handlePayClick = async () => {
setPayProcessing(true)
setPayError('')
try {
const hireEnd = new Date(payModal.start_date)
hireEnd.setDate(hireEnd.getDate() + Math.ceil((payModal.hours || 0) / 24))
const daysToEnd = Math.ceil((hireEnd - new Date()) / (1000 * 60 * 60 * 24))
if (daysToEnd > 5) {
const earliest = new Date(hireEnd)
earliest.setDate(earliest.getDate() - 5)
setPayError(`Payment can't be taken this far ahead — the hold on your card would expire before your hire ends. You can pay from ${earliest.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}.`)
setPayProcessing(false)
return
}
const { data: ownerProfile } = await supabase.from('public_profiles').select('stripe_account_id, stripe_charges_enabled').eq('id', payModal.owner_id).single()
if (!ownerProfile?.stripe_charges_enabled) {
setPayError("The owner hasn't finished setting up payments yet. Please message them.")
setPayProcessing(false)
return
}
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${BACKEND_URL}/stripe/payment-intent`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
amountAUD: payModal.total_amount,
depositAUD: payModal.deposit_amount || 0,
ownerStripeId: ownerProfile.stripe_account_id,
bookingId: payModal.id,
listingTitle: payModal.listings?.title,
})
})
const data = await res.json()
if (data.clientSecret) {
await supabase.from('bookings').update({ stripe_payment_intent_id: data.paymentIntentId }).eq('id', payModal.id)
window.location.href = `/payment?booking=${payModal.id}&secret=${data.clientSecret}`
} else {
setPayError('Payment setup failed. Please try again.')
}
} catch (e) {
setPayError('Payment error: ' + e.message)
}
setPayProcessing(false)
}

// Cancel preview for modal
const cancelPreview = cancelModal ? calculateRefund(cancelModal, cancelModal.hirer_id === user?.id) : null

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 700, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
<h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 0, color: 'var(--navy)' }}>My bookings</h1>
</div>
<div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
{[{ key: 'owner', label: 'Booking requests' }, { key: 'hirer', label: 'Items I hired' }].map(t => (
<button key={t.key} className={`btn ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(t.key)}>{t.label}</button>
))}
</div>

{tab === 'owner' && profile && !profile.email_verified && (
<VerifyEmailPrompt reason="my_bookings" style={{ marginBottom: '1.5rem' }} />
)}

{offers.length > 0 && (
<div style={{ marginBottom: '1.5rem' }}>
<h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>
💰 {tab === 'owner' ? 'Offers received' : 'My offers'} ({offers.length})
</h2>
<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
{offers.map(offer => (
<div key={offer.id} className="card card-body" style={{ border: '2px solid var(--amber)' }}>
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
<div>
<div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--navy)' }}>{offer.listings?.title}</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{tab === 'owner' ? `From: ${offer.hirer?.full_name}` : `To: ${offer.owner?.full_name}`}
</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{offer.start_date} · {offer.hours} hrs
</div>
</div>
<div style={{ textAlign: 'right' }}>
<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--amber)' }}>
${offer.offered_price}/hr
</div>
<div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
Listed: ${offer.listings?.price_per_hour}/hr
</div>
</div>
</div>
{offer.notes && (
<div style={{ fontSize: 13, color: 'var(--text-muted)', background: 'var(--gray-50)', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
"{offer.notes}"
</div>
)}
<div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 8 }}>
⏱ Expires {new Date(offer.expires_at).toLocaleString('en-AU')}
</div>
{tab === 'owner' && (
<div style={{ display: 'flex', gap: 8 }}>
<button className="btn btn-primary btn-sm" onClick={() => handleAcceptOffer(offer)} disabled={processing}>
✅ Accept offer
</button>
<button className="btn btn-danger btn-sm" onClick={() => handleDeclineOffer(offer)} disabled={processing}>
❌ Decline
</button>
</div>
)}
{tab === 'hirer' && (
<span className="badge badge-amber">⏳ Awaiting response</span>
)}
</div>
))}
</div>
</div>
)}

{loading ? <div className="spinner-wrap"><div className="spinner" /></div> :
bookings.length === 0 ? (
<div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
<div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
<p>{tab === 'hirer' ? 'No bookings yet. Find something to hire!' : 'No booking requests yet.'}</p>
<button className="btn btn-gradient" style={{ marginTop: 16 }} onClick={() => navigate(tab === 'hirer' ? '/' : '/list-item')}>
{tab === 'hirer' ? 'Browse listings' : 'List an item'}
</button>
</div>
) : (
<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
{bookings.map(b => {
const s = BOOKING_STATUS[b.status] || BOOKING_STATUS.pending
const isPending = b.status === 'pending'
const isConfirmed = b.status === 'confirmed'
const isCompleted = b.status === 'completed'
const isCancelled = b.status === 'cancelled'
const isPaid = !!b.paid_at
const hasReviewed = reviewedBookings.includes(b.id)
const hasDisputed = disputedBookings.includes(b.id)
const canCancel = (isPending || isConfirmed) && !b.picked_up_at && !isCancelled

return (
<div key={b.id} className="card card-body" style={isPaid && isConfirmed ? { borderLeft: '4px solid #16A34A' } : isCancelled ? { borderLeft: '4px solid #DC2626' } : {}}>
{hasDisputed && (
<div className="alert alert-warn" style={{ marginBottom: 12, fontSize: 13 }}>
⚠️ Problem reported — our team is reviewing this booking.
</div>
)}
{isCancelled && (
<div style={{ background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991B1B' }}>
❌ Booking cancelled {b.cancelled_by ? `by ${b.cancelled_by}` : ''}
{b.refund_owed && <div style={{ marginTop: 4 }}>💰 Refund: ${b.refund_owed.toFixed(2)}</div>}
</div>
)}
{isPaid && isConfirmed && (
<div style={{ background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#15803D', fontWeight: 600 }}>
✅ Payment received{tab === 'owner' ? ` from ${b.hirer?.full_name}` : ''} — arrange pickup via messages
</div>
)}
<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
<div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
{b.listings?.images?.[0] ? (
<img src={b.listings.images[0]} alt={b.listings?.title} loading="lazy" style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
) : (
<ListingPlaceholder listing={b.listings} size={52} radius={12} />
)}
<div>
<div
style={titleLinkStyle}
onClick={() => { if (b.listing_id) navigate(`/listing/${b.listing_id}`) }}
onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
>
{b.listings?.title}
</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
{tab === 'owner' ? `Hirer: ${b.hirer?.full_name}` : `Owner: ${b.owner?.full_name}`}
</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
{formatDate(b.start_date)} · {b.hours}hrs · {b.listings?.postcode}
</div>
</div>
</div>
<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
<div style={{ fontWeight: 700, color: 'var(--navy)' }}>{formatCurrencyByCountry(b.total_amount, b.listings?.country || 'Australia')}</div>
{isPaid && isConfirmed ? (
<span className="badge badge-green" style={{ background: '#16A34A', color: 'white' }}>✅ Paid</span>
) : (
<span className="badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>
)}
</div>
</div>

<div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
{isPending && tab === 'owner' && (
<>
<button className="btn btn-primary btn-sm" onClick={() => handleAccept(b)} disabled={processing}>✅ Accept</button>
<button className="btn btn-danger btn-sm" onClick={() => setDeclineModal(b)} disabled={processing}>❌ Decline</button>
</>
)}

{isPending && tab === 'hirer' && (
<span className="badge badge-amber">⏳ Awaiting owner response</span>
)}

{isConfirmed && tab === 'hirer' && !isPaid && (
<button className="btn btn-gradient btn-sm" onClick={() => setPayModal(b)}>
💳 Pay now
</button>
)}
{isConfirmed && tab === 'owner' && (
<button className="btn btn-ghost btn-sm" onClick={() => handleComplete(b)} disabled={processing}>
✓ Mark as completed
</button>
)}

{canCancel && (
<button className="btn btn-ghost btn-sm" onClick={() => setCancelModal(b)} style={{ color: 'var(--red)' }}>
❌ Cancel booking
</button>
)}

{(isConfirmed || isCompleted) && tab === 'hirer' && !hasDisputed && (
<button className="btn btn-ghost btn-sm" onClick={() => setDisputeModal(b)} style={{ color: 'var(--red)' }}>
⚠️ Report a problem
</button>
)}

{isCompleted && !hasReviewed && (
<button className="btn btn-primary btn-sm" onClick={() => setReviewModal(b)}>
⭐ Leave a review
</button>
)}

{isCompleted && hasReviewed && (
<span className="badge badge-green">⭐ Review submitted</span>
)}

<button className="btn btn-ghost btn-sm" onClick={() => navigate(`/messages?booking=${b.id}`)}>
💬 Message {tab === 'owner' ? 'hirer' : 'owner'}
</button>
</div>
</div>
)
})}
</div>
)}
</div>

{cancelModal && cancelPreview && (
<div className="modal-overlay" onClick={() => setCancelModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Cancel this booking?</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Once cancelled, this booking cannot be reinstated.
</p>

<div style={{ background: cancelPreview.policy === 'unpaid' ? 'var(--gray-50)' : cancelPreview.feeAmount > 0 ? '#FFF8E7' : '#E8F8EE', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
{cancelPreview.policy === 'unpaid' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4 }}>💸 No payment yet</div>
<div>You haven't been charged. Cancelling is free.</div>
</>
)}
{cancelPreview.policy === 'owner-cancel' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#15803D' }}>💰 Owner cancellation — full refund</div>
<div>Hirer will receive a full refund of <strong>${cancelPreview.refundAmount.toFixed(2)}</strong>.</div>
</>
)}
{cancelPreview.policy === '48+hr' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#15803D' }}>💰 Full refund — 48+ hours notice</div>
<div>You'll receive <strong>${cancelPreview.refundAmount.toFixed(2)}</strong> back.</div>
</>
)}
{cancelPreview.policy === '24-48hr' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#8a6d00' }}>⚠️ 50% refund — 24-48 hours notice</div>
<div>Refund: <strong>${cancelPreview.refundAmount.toFixed(2)}</strong> · Cancellation fee: <strong>${cancelPreview.feeAmount.toFixed(2)}</strong></div>
</>
)}
{cancelPreview.policy === 'under-24hr' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#991B1B' }}>⚠️ no refund — under 24 hours notice</div>
<div>Refund: <strong>${cancelPreview.refundAmount.toFixed(2)}</strong> · Cancellation fee: <strong>${cancelPreview.feeAmount.toFixed(2)}</strong></div>
</>
)}
</div>

<div className="form-group">
<label className="form-label">Reason (optional, visible to other party)</label>
<textarea
className="form-control"
value={cancelReason}
onChange={e => setCancelReason(e.target.value)}
placeholder="e.g. Change of plans, item no longer available..."
style={{ minHeight: 70 }}
/>
</div>

<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
<button className="btn btn-ghost" onClick={() => { setCancelModal(null); setCancelReason('') }} disabled={cancelling}>Keep booking</button>
<button className="btn btn-danger" onClick={handleCancelBooking} disabled={cancelling}>
{cancelling ? 'Cancelling...' : '❌ Cancel booking'}
</button>
</div>
</div>
</div>
)}

{verifyBlockModal && (
<div className="modal-overlay" onClick={() => setVerifyBlockModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>One quick step before you accept</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 12 }}>
To accept {verifyBlockModal.hirerName ? `${verifyBlockModal.hirerName}'s` : 'this'} request, please confirm your email address first. The request will stay here waiting — come back and tap Accept once you've clicked the link.
</p>
<VerifyEmailPrompt reason="accept" style={{ marginBottom: 12 }} />
<button className="btn btn-ghost btn-full" onClick={() => setVerifyBlockModal(null)}>Close</button>
</div>
</div>
)}

{stripeBlockModal && (
<div className="modal-overlay" onClick={() => setStripeBlockModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<div style={{ fontSize: 40, marginBottom: 8 }}>💳</div>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Connect your bank account to accept</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
{stripeBlockModal.hirerName || 'The hirer'} can't pay until your bank account is connected, so you'll need to do this before accepting. It's done securely through Stripe and takes about 5 minutes — have your driver's licence and bank details handy.
</p>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
The request will stay here waiting. Come back to My Bookings to accept it once you're connected.
</p>
<button className="btn btn-gradient btn-full" onClick={() => navigate('/payouts')}>Connect bank account</button>
<button className="btn btn-ghost btn-full" style={{ marginTop: 8 }} onClick={() => setStripeBlockModal(null)}>Not now</button>
</div>
</div>
)}

{payModal && (
<div className="modal-overlay" onClick={() => setPayModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>💳 Pay for booking</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Complete payment for <strong>{payModal.listings?.title}</strong>
</p>
<div style={{ background: 'var(--primary-pale)', borderRadius: 12, padding: '1rem', marginBottom: 16 }}>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 6 }}>
<span>Hire total</span>
<span style={{ fontWeight: 700, color: 'var(--navy)' }}>{formatCurrencyByCountry(payModal.total_amount, payModal.listings?.country || 'Australia')}</span>
</div>
<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)' }}>
<span>Security deposit (refundable)</span>
<span>{formatCurrencyByCountry(payModal.deposit_amount || 0, payModal.listings?.country || 'Australia')}</span>
</div>
</div>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
You will be redirected to our secure payment page to authorise your payment via Stripe.
</p>
{payError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{payError}</div>}
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setPayModal(null)}>Cancel</button>
<button className="btn btn-gradient" disabled={payProcessing} onClick={handlePayClick}>
{payProcessing ? 'Processing...' : `Pay ${formatCurrencyByCountry(payModal.total_amount, payModal.listings?.country || 'Australia')}`}
</button>
</div>
</div>
</div>
)}

{declineModal && (
<div className="modal-overlay" onClick={() => setDeclineModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Decline booking</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Select a reason or write your own. This will be sent as a message to the hirer.
</p>
<div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
{DECLINE_REASONS.map(reason => (
<label key={reason} style={reasonLabel(selectedReason === reason && !customReason)}>
<input type="radio" name="reason" checked={selectedReason === reason && !customReason} onChange={() => { setSelectedReason(reason); setCustomReason('') }} />
{reason}
</label>
))}
</div>
<div className="form-group">
<label className="form-label">Or write a custom message:</label>
<textarea className="form-control" placeholder="Write your own decline message..." value={customReason} onChange={e => setCustomReason(e.target.value)} style={{ minHeight: 80 }} />
</div>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
<button className="btn btn-ghost" onClick={() => { setDeclineModal(null); setCustomReason('') }}>Cancel</button>
<button className="btn btn-danger" onClick={handleDecline} disabled={processing}>
{processing ? 'Declining...' : 'Decline booking'}
</button>
</div>
</div>
</div>
)}

{disputeModal && (
<div className="modal-overlay" onClick={() => setDisputeModal(null)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--navy)' }}>⚠️ Report a problem</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Tell us what went wrong with <strong>{disputeModal.listings?.title}</strong>. Our team will review and contact you within 24 hours regarding a possible refund.
</p>
<div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
{PROBLEM_REASONS.map(reason => (
<label key={reason} style={reasonLabel(selectedProblem === reason && !customProblem)}>
<input type="radio" name="problem" checked={selectedProblem === reason && !customProblem} onChange={() => { setSelectedProblem(reason); setCustomProblem('') }} />
{reason}
</label>
))}
</div>
<div className="form-group">
<label className="form-label">Additional details (optional):</label>
<textarea className="form-control" placeholder="Describe what happened in more detail..." value={customProblem} onChange={e => setCustomProblem(e.target.value)} style={{ minHeight: 80 }} />
</div>
<div className="alert alert-warn" style={{ fontSize: 13, marginTop: 12 }}>
<strong>Note:</strong> Refunds are assessed case by case. HireIt will contact both parties to resolve the dispute fairly.
</div>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
<button className="btn btn-ghost" onClick={() => setDisputeModal(null)}>Cancel</button>
<button className="btn btn-danger" onClick={handleDispute} disabled={processing}>
{processing ? 'Submitting...' : 'Submit report'}
</button>
</div>
</div>
</div>
)}

{reviewModal && (
<ReviewModal
booking={reviewModal}
user={user}
onClose={() => setReviewModal(null)}
onSubmit={handleReviewSubmit}
/>
)}

{showReferralModal && (
<div className="modal-overlay" onClick={() => setShowReferralModal(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<div style={{ textAlign: 'center', marginBottom: 20 }}>
<div style={{ fontSize: 48, marginBottom: 8, lineHeight: 1 }}>🎉</div>
<h3 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', marginBottom: 8 }}>
Nice work — your hire's complete!
</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 0, lineHeight: 1.5 }}>
Refer a friend to HireIt. When they complete their first hire, you'll get your next 3 hires at a reduced 10% platform fee instead of 12%.
</p>
</div>
{referralLink && (
<div style={{ marginBottom: 20 }}>
<div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Your referral link</div>
<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
<input
readOnly
value={referralLink}
style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
/>
<button
onClick={copyReferralLink}
style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: 'white', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
>
{referralCopied ? 'Copied!' : 'Copy'}
</button>
</div>
</div>
)}
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setShowReferralModal(false)}>
Maybe later
</button>
<button
className="btn btn-gradient"
onClick={() => { setShowReferralModal(false); navigate('/referrals') }}
>
View my referrals
</button>
</div>
</div>
</div>
)}
</div>
)
}
